import { FormattedMessage } from 'react-intl';
import * as dealInfoHelpers from 'shared/utils/deal-info-helpers';
import defaultSettings from 'default-settings';
import autobind from 'core-decorators/lib/autobind';
import { notForPreview } from 'widget/utils/not-for-preview';

import { openFullScreenSubscribeOverlay, openFullScreenPurchaseOverlay } from 'shared/utils/open-overlay';

import {
  getBuyButtonDisplayTypeWhenInOverlay,
  shouldShowChannelCover,
} from 'widget/utils/overlay-buttons-utils';
import { getPlayButtonTypeString } from 'shared/utils/bi/video-overlay-events-params';
import { isPreviewMode, openPreviewPrompt } from 'widget/components/preview-prompt/preview-prompt';
import { canAccessFullVideo } from 'widget/ui-selectors/video-access';

import renderVideoPlayButtonText from 'widget/components/overlay-texts/video/play-button-text';
import renderVideoSignInToWatchButtonText from 'widget/components/overlay-texts/video/sign-in-to-watch-button-text';
import renderVideoSubscribeButtonText from 'widget/components/overlay-texts/video/subscribe-button-text';
import renderChannelPlayButtonText from 'widget/components/overlay-texts/channel/play-button-text';
import renderChannelSignInToWatchButtonText from 'widget/components/overlay-texts/channel/sign-in-to-watch-button-text';
import renderChannelSubscribeButtonText from 'widget/components/overlay-texts/channel/subscribe-button-text';
import renderOpenVideoWithoutPreviewButtonText from 'widget/components/overlay-texts/channel/open-video-without-preview-button-text';
import renderPurchaseButtonText from 'widget/components/overlay-texts/purchase-button-text';

import Title from './title/title';

import commonTranslations from 'shared/translations/overlay-common-translations';


export default class BaseOverlay extends React.Component {

  static propTypes = {
    channelData: React.PropTypes.object,
    videoItem: React.PropTypes.object,
    appSettings: React.PropTypes.object.isRequired,
    currentSiteUser: React.PropTypes.object,
    showChannelCover: React.PropTypes.bool,
    beenPlayed: React.PropTypes.bool,
    ended: React.PropTypes.bool,
    className: React.PropTypes.string,
    onPlayClick: React.PropTypes.func,
    playSelectedVideo: React.PropTypes.func,
    playVideoItem: React.PropTypes.func, // comes dome player
    playNextVideo: React.PropTypes.func,
    nextVideoId: React.PropTypes.string,
    biEvent: React.PropTypes.func,
    canShowMainItemPublisher: React.PropTypes.bool,
    canShowMainItemTitle: React.PropTypes.bool,
  };

  static defaultProps = {
    beenPlayed: false,
    ended: false,
    showChannelCover: false,
    onPlayClick: _.noop,
    playSelectedVideo: _.noop,
    playNextVideo: _.noop,
  };

  clampOptions = {
    lineClamp: 2,
  };

  get subscriptionPriceInfo() {
    return dealInfoHelpers.getSubscriptionPriceInfo(this.props.channelData.dealInfo);
  }

  get minVideoPriceInfo() {
    const { videoItem } = this.props;
    if (!videoItem) {
      return null;
    }
    return dealInfoHelpers.getMinPriceInfo(videoItem.dealInfo);
  }

  get saleDealInfo() {
    const { videoItem } = this.props;
    if (!videoItem) {
      return null;
    }
    return dealInfoHelpers.getSalePriceInfo(videoItem.dealInfo);
  }

  get rentDealInfo() {
    const { videoItem } = this.props;
    if (!videoItem) {
      return null;
    }
    return dealInfoHelpers.getRentPriceInfo(videoItem.dealInfo);
  }

  get hasNextVideo() {
    const { nextVideoId } = this.props;
    return Boolean(nextVideoId);
  }

  get titleText() {
    const { channelData, videoItem } = this.props;
    return this.isChannelCoverView ? channelData.title : videoItem.title;
  }

  get publisherText() {
    if (this.isChannelCoverView) {
      return null;
    }

    const { videoItem } = this.props;
    return videoItem.publisher;
  }

  get shouldShowTitle() {
    return this.props.canShowMainItemTitle;
  }

  get title() {
    if (!this.shouldShowTitle) {
      return null;
    }

    const titleText = this.titleText;

    if (!titleText) {
      return null;
    }

    return (
      <Title styleName="title"
             text={titleText}
             clampOptions={this.clampOptions} />
    );
  }

  get publisher() {
    if (!this.props.canShowMainItemPublisher) {
      return null;
    }

    const publisherText = this.publisherText;

    if (!publisherText) {
      return null;
    }

    return (
      <div styleName="publisher"
           className="qa-widget-overlay-publisher">
        {publisherText}
      </div>
    );
  }

  get isChannelCoverView() {
    const { appSettings, showChannelCover } = this.props;
    // Todo: move to connect
    return shouldShowChannelCover(appSettings, showChannelCover);
  }

  get buttonTextProps() {
    const { channelData, videoItem, currentSiteUser } = this.props;

    return {
      channel: channelData,
      video: videoItem,
      currentSiteUser,
    };
  }

  get playButtonText() {
    const props = this.buttonTextProps;

    // Todo: move each button type to separate methods and use at views
    if (this.isChannelCoverView) {
      return renderChannelPlayButtonText(props) ||
        renderChannelSignInToWatchButtonText(props) ||
        renderOpenVideoWithoutPreviewButtonText(props);
    }
    return renderVideoPlayButtonText(props) || renderVideoSignInToWatchButtonText(props);
  }

  get playNextButtonText() {
    const { ended } = this.props;

    if (ended && !this.minVideoPriceInfo && this.hasNextVideo) {
      return <FormattedMessage {...commonTranslations.nextVideo} />;
    }

    return null;
  }

  get subscribeButtonText() {
    // Todo: move each button type to separate methods and use at views

    const props = this.buttonTextProps;
    if (this.isChannelCoverView) {
      return renderChannelSubscribeButtonText(props);
    }

    return renderVideoSubscribeButtonText(props);
  }

  get purchaseButtonText() {
    if (this.isChannelCoverView) {
      return null;
    }
    return renderPurchaseButtonText(this.buttonTextProps);
  }

  sendPlayButtonBI() {
    const { channelData, videoItem, biEvent, currentSiteUser } = this.props;
    if (this.isChannelCoverView) {
      const button = canAccessFullVideo(channelData, videoItem, currentSiteUser) ?
        'startWatching' :
        'watchPreview';

      sendChCoverButtonClickedBI(this.props, button);
    } else {
      sendVideoButtonClickedBI(this.props, getPlayButtonTypeString(channelData, videoItem, currentSiteUser));
    }

    const { id, videoSource } = videoItem;
    biEvent('widget.vid.clicked', {
      channelID: channelData.id,
      videoID: id,
      videoType: videoSource,
    });
  }

  // TODO: this component should call prop onPlayClick without additional logic. Move to view
  @autobind
  playSelectedVideo(event) {
    event.preventDefault();
    event.stopPropagation();

    const { playSelectedVideo, videoItem } = this.props;

    if (isPreviewMode() && videoItem.memberOnly) {
      openPreviewPrompt();
      return;
    }

    this.sendPlayButtonBI();

    playSelectedVideo(videoItem);
  }

  @autobind
  playNextVideo(event) {
    event.preventDefault();
    event.stopPropagation();

    this.props.playNextVideo();
  }

  sendSubscribeClickedBI() {
    sendChCoverButtonClickedBI(this.props, 'subscribe');
  }

  @autobind
  @notForPreview
  openSubscription(event) {
    event.preventDefault();
    event.stopPropagation();
    this.sendSubscribeClickedBI();

    const { channelData, biEvent } = this.props;

    openFullScreenSubscribeOverlay(channelData.id, () => {
      biEvent('widget.subscriptionPopUp.Completed', { result: 'canceled', errorMsg: '' });
    });
  }

  @autobind
  @notForPreview
  openPurchase(event) {
    event.preventDefault();
    event.stopPropagation();
    this.sendOpenPurchaseBI();
    const { channelData, videoItem } = this.props;

    openFullScreenPurchaseOverlay(channelData.id, videoItem.id);
  }

  sendOpenPurchaseBI() {
    sendVideoButtonClickedBI(this.props, getBuyButtonDisplayTypeWhenInOverlay(this.props));
  }

  getOpacityStyles() {
    const { appSettings } = this.props;
    const opacityPercents = _.isNumber(appSettings.numbers.mainItemOverlayOpacity) ?
      appSettings.numbers.mainItemOverlayOpacity :
      defaultSettings.numbers.mainItemOverlayOpacity;
    const opacity = _.round(opacityPercents / 100, 2);

    return {
      backgroundColor: `rgba(0,0,0,${opacity})`,
    };
  }
}


// FIXME: all components should connect BE for that, it's really not expected
function sendChCoverButtonClickedBI(props, button) {
  const { biEvent } = props;

  biEvent('widget.chCover.btn.clicked', { button });
}

function sendVideoButtonClickedBI(props, button) {
  const { videoItem, biEvent } = props;

  biEvent('widget.VidSelected.btn.clicked', {
    button,
    buttonType: 'main',
    videoID: videoItem.id,
  });
}



// WEBPACK FOOTER //
// ./src/widget/components/player-overlay/_base-overlay.js